import { useMutation } from '@apollo/client';
import {
  updateUnit as updateUnitMutation,
  updateUnitAndReferencesMutation,
  getUnitAndReferences,
  getUnit,
} from 'lane-shared/graphql/units';
import { listPropertyFloors } from 'lane-shared/graphql/floors';
import { calculateExternalReferenceDifferences } from '../../calculations/externalReferences';

import { getUpdatedExternalReferences } from '../../calculations/getUpdatedExternalReferences';
import { UpdateUnitProps } from '../../types';

export const useUpdateUnit = (isExternalReferenceEnabled: boolean = false) => {
  const [updateUnitMutationFn, { loading: loadingUpdateUnit }] = useMutation(
    updateUnitMutation,
    {
      update: (cache, { data }, { variables }) => {
        if (!data || !variables) {
          return;
        }

        const existingUnit = cache.readQuery({
          query: getUnit,
          variables,
        });

        const existingFloors = cache.readQuery({
          query: listPropertyFloors,
          variables,
        });

        cache.writeQuery({
          query: getUnit,
          variables,
          data: {
            unit: {
              // Replace the entire unit record in the cache to instantly show all updates
              id: data.updateUnit,
              name: variables.unit.name,
              description: variables.unit.description,
              propertyId: variables.propertyId,
              floors:
                variables.unit.floors?.map((floor: string) => {
                  const found = existingFloors?.property?.floors?.find(
                    f => f?.id === floor
                  );

                  return found
                    ? { id: found.id, name: found.name, index: found.index }
                    : null;
                }) ?? [],
              people: existingUnit?.unit?.people ?? [],
              tenants: existingUnit?.unit?.tenants ?? [],
            },
          },
        });
      },
    }
  );

  const [
    updateUnitAndReferencesMutationFn,
    { loading: loadingUpdateUnitAndReferences },
  ] = useMutation(updateUnitAndReferencesMutation, {
    update: (cache, { data }, { variables }) => {
      if (!data || !variables) {
        return;
      }

      const existingUnit = cache.readQuery({
        query: getUnitAndReferences,
        variables,
      });

      const existingFloors = cache.readQuery({
        query: listPropertyFloors,
        variables,
      });

      let externalReferences = existingUnit?.unit?.externalReferences ?? [];

      if (
        data?.removeUnitExternalReferences?.length &&
        !data?.addUnitExternalReferences?.length
      ) {
        externalReferences = [];
      } else if (data?.addUnitExternalReferences?.length) {
        externalReferences = data.addUnitExternalReferences;
      }

      const floors =
        variables.unit.floors?.map((floor: string) => {
          const found = existingFloors?.property?.floors?.find(
            f => f?.id === floor
          );

          return found
            ? { id: found.id, name: found.name, index: found.index }
            : null;
        }) ?? [];

      cache.writeQuery({
        query: getUnitAndReferences,
        variables,
        data: {
          unit: {
            // Replace the entire unit & external references records in the cache to instantly show all updates
            id: data.updateUnit,
            name: variables.unit.name,
            description: variables.unit.description,
            propertyId: variables.propertyId,
            floors,
            externalReferences,
            people: existingUnit?.unit?.people ?? [],
            tenants: existingUnit?.unit?.tenants ?? [],
          },
        },
      });
    },
  });

  const loading = isExternalReferenceEnabled
    ? loadingUpdateUnitAndReferences
    : loadingUpdateUnit;

  const _updateUnit = (props: UpdateUnitProps) => {
    return updateUnitMutationFn({
      variables: {
        propertyId: props.propertyId,
        unitId: props.unitId,
        unit: {
          name: props.unit.name,
          description: props.unit.description,
          floors: props.unit.floors.map((floor: { value: any }) => floor.value),
        },
      },
    });
  };

  const updateUnitAndReferences = (props: UpdateUnitProps) => {
    const existingReferences = props.existingExternalReferences ?? [];
    const newExternalReferences = getUpdatedExternalReferences(props);

    // Calculate differences
    const { referencesToAdd, referencesToRemove } =
      calculateExternalReferenceDifferences(
        existingReferences,
        newExternalReferences
      );

    return updateUnitAndReferencesMutationFn({
      variables: {
        propertyId: props.propertyId,
        unitId: props.unitId,
        unit: {
          name: props.unit.name,
          description: props.unit.description,
          floors: props.unit.floors.map((floor: { value: any }) => floor.value),
        },
        addExternalReferences: referencesToAdd,
        removeExternalReferences: referencesToRemove,
      },
    });
  };

  const updateUnit = async (props: UpdateUnitProps) => {
    if (isExternalReferenceEnabled) {
      await updateUnitAndReferences(props);

      return;
    }

    await _updateUnit(props);
  };

  return {
    updateUnit,
    loading,
  };
};
